'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { SmartLink } from '@benzinga/analytics';
import { SocialButtons } from '@benzinga/ui';
import { socialButtons } from './mock';
import {
  FusionSection,
  FusionSectionCaption,
  FusionSectionContent,
  FusionSectionText,
  FusionSectionTitle,
} from '@benzinga/ui';
import { Location } from '@benzinga/themed-icons';

const OurContacts = () => {
  return (
    <StyledSection>
      <StyledSectionContent whiteBackground>
        <FusionSectionCaption>Our Contacts</FusionSectionCaption>
        <FusionSectionTitle>How Can I Contact Benzinga?</FusionSectionTitle>
        <StyledSectionText>
          You can contact us directly by going to our{' '}
          <Link href="https://www.benzinga.com/contact">Contact Us page</Link>. We will be sure to get to you with a
          response within hours. If you have specific questions, feel free to use the below list to get a hold of us.
        </StyledSectionText>
        <Contacts>
          <ContactsColumn>
            <ContactsTitle>Customer Support</ContactsTitle>
            <ContactsText>
              support@benzinga.com <br />
              1-877-440-ZING (9464)
            </ContactsText>
          </ContactsColumn>
          <ContactsColumn>
            <ContactsTitle>Contribute To Benzinga</ContactsTitle>
            <ContactsText>
              To submit an article, go to <br />
              <Link href="https://contributor.benzinga.com/contributor-onboarding">contributor.benzinga.com</Link>
            </ContactsText>
          </ContactsColumn>
        </Contacts>

        <Social buttons={socialButtons} />
        <Footer>
          <IconBox>
            <InactiveLocation />
          </IconBox>
          <LocationCaption>Today the company is headquartered in</LocationCaption>
          <LocationTitle>Detroit, Michigan</LocationTitle>
          <LocationAddress>Attn: Benzinga, 1 Campus Martius Suite 200, Detroit MI 48226</LocationAddress>
        </Footer>
        <Image alt="man with laptop" src="/next-assets/images/about/about-us.png" />
      </StyledSectionContent>
    </StyledSection>
  );
};

const StyledSection = styled(FusionSection)`
  padding-bottom: 80px;
  @media screen and (max-width: 567px) {
    padding-bottom: 48px;
  }
`;

const StyledSectionContent = styled(FusionSectionContent)`
  position: relative;
`;

const StyledSectionText = styled(FusionSectionText)`
  max-width: 550px;
`;

const Contacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
  margin-top: 80px;
  @media screen and (max-width: 959px) {
    margin-top: 0;
  }
  @media screen and (max-width: 567px) {
    gap: 24px;
  }
`;

const ContactsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media screen and (max-width: 567px) {
    gap: 12px;
  }
`;

const ContactsTitle = styled.div`
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  color: ${props => props.theme.colorPalette.gray400};
`;

const ContactsText = styled.div`
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.normal};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight[6]};
  color: ${props => props.theme.colorPalette.gray700};
`;

const Link = styled(SmartLink)`
  color: ${props => props.theme.colorPalette.blue500};
`;

const Social = styled(SocialButtons)`
  margin-top: 40px;
`;

const Footer = styled.div`
  position: relative;
  display: grid;
  align-items: center;
  padding-top: 48px;
  column-gap: 24px;
  row-gap: 4px;
  grid-template-columns: 60px auto;
  grid-template-areas:
    'icon caption '
    'icon title'
    'icon address';
  @media screen and (max-width: 767px) {
    grid-template-columns: 40px auto;
    grid-template-rows: auto auto 0 auto;
    grid-template-areas:
      'icon caption'
      'icon title'
      '. .'
      'address address';
  }

  @media screen and (max-width: 567px) {
    padding-top: 40px;
  }

  &:after {
    content: '';
    position: absolute;
    top: 24px;
    left: -24px;
    right: -24px;
    height: 1px;
    background-color: ${props => props.theme.colorPalette.gray100};
    @media screen and (max-width: 567px) {
      left: -16px;
      right: -16px;
    }
  }
`;

const InactiveLocation = styled(Location)`
  @media screen and (max-width: 767px) {
    width: 12px;
  }
`;

const IconBox = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  width: 64px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.theme.colorPalette.gray50};
  @media screen and (max-width: 767px) {
    height: 40px;
    width: 40px;
  }
`;

const LocationCaption = styled.div`
  grid-area: caption;
  font-weight: ${({ theme }) => theme.fontWeight.normal};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight[4]};
  color: ${({ theme }) => theme.colorPalette.gray400};
`;

const LocationTitle = styled.div`
  grid-area: title;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorPalette.gray700};
`;

const LocationAddress = styled.div`
  grid-area: address;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.base};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorPalette.gray500};
`;

const Image = styled.img`
  position: absolute;
  right: 54px;
  width: 575px;
  bottom: 0;
  @media screen and (max-width: 1279px) {
    width: 512px;
    right: -48px;
  }
  @media screen and (max-width: 959px) {
    display: none;
  }
`;

export default OurContacts;
